// src/components/Layers/ExpensesLayer.jsx
import React, { useEffect, useState, useRef, useMemo } from 'react';
import { Source, Layer, useMap } from 'react-map-gl';
import '../../components/Markers/MapMarkers.css';
import { useNavigate} from 'react-router-dom';
import Select, { components } from 'react-select';
import axios from 'axios';
import API_BASE_URL from '../../config';

export default function ExpensesLayer() {
  const map = useMap().current;
  const navigate = useNavigate();
  const STORAGE_ADDED_KEY = 'addedHouses';
  const ONE_HOUR          = 60 * 60 * 1000;

  // Сохраняем выбранные серии в localStorage
  const [selectedSeries, setSelectedSeries] = useState(() => {
    try {
      const saved = localStorage.getItem('selectedSeries');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem('selectedSeries', JSON.stringify(selectedSeries));
    } catch {}
  }, [selectedSeries]);

  // Список серий для фильтрации
  const [seriesList, setSeriesList] = useState([]);
  const seriesOptions = useMemo(
    () => seriesList.map((s) => ({ value: s.id, label: s.name })),
    [seriesList]
  );

  // GeoJSON с позициями домов (только координаты и id)
  const [geoData, setGeoData] = useState(null);

  // Состояние выбранного дома (данные, полученные из get_build_by_id)
  const [selectedHouse, setSelectedHouse] = useState(null);
  const [animatePanel, setAnimatePanel] = useState(false);
  const hasAnimated = useRef(false);
  const selectedHouseId = useRef(null);

  // При монтировании загружаем список серий и координаты домов
  useEffect(() => {
    // Получаем список серий
    axios
      .get(`${API_BASE_URL}/building/series_all`)
      .then((res) => setSeriesList(res.data))
      .catch(console.error);

    // Получаем все дома, чтобы получить их координаты и id
    axios
      .get(`${API_BASE_URL}/building/get_all_build`)
      .then((res) => {
        const data = res.data;

        /* ---------------------------------
          1️⃣  Точки, пришедшие с бек-энда
          ---------------------------------*/
        const apiFeatures = data.map((house) => ({
          type: 'Feature',
          id: house.id,
          geometry: {
            type: 'Point',
            coordinates: [Number(house.longitude), Number(house.latitude)],
          },
          properties: {
            id:        house.id,
            series_id: house.series_id,
          },
        }));

        /* ---------------------------------
          2️⃣  Оптимистичные точки из localStorage
          ---------------------------------*/
        let optimisticFeatures = [];
        try {
          const now    = Date.now();
          const stored = JSON.parse(localStorage.getItem(STORAGE_ADDED_KEY) || '[]')
                          .filter((h) => now - h.timestamp < ONE_HOUR);

          /* очищаем просроченные записи */
          localStorage.setItem(STORAGE_ADDED_KEY, JSON.stringify(stored));

          optimisticFeatures = stored
            /* убираем те, что уже пришли с API */
            .filter((h) => !apiFeatures.some((f) => f.id === h.id))
            /* собираем GeoJSON-фичи */
            .map((h) => ({
              type: 'Feature',
              id:   h.id,
              geometry: {
                type: 'Point',
                coordinates: [Number(h.longitude), Number(h.latitude)],
              },
              properties: {
                id:        h.id,
                series_id: h.series_id,
                _optimistic: true,        // пометка (можно использовать в стиле)
              },
            }));
        } catch {/* ignore */}

        /* ---------------------------------
          3️⃣  Склеиваем и фильтруем скрытый дом
          ---------------------------------*/
        let features = [...apiFeatures, ...optimisticFeatures];

        const hiddenHouse = JSON.parse(localStorage.getItem('hiddenHouse'));
        if (hiddenHouse && Date.now() < hiddenHouse.expiry) {
          features = features.filter((f) => f.id !== hiddenHouse.id);
        } else {
          localStorage.removeItem('hiddenHouse');
        }

        setGeoData({ type: 'FeatureCollection', features });
      })
      .catch((err) => console.error('Ошибка загрузки домов:', err));
  }, [ONE_HOUR]);

  // Фильтрация geoData по выбранным сериям
  const filteredGeoData = useMemo(() => {
    if (!geoData) return null;
    if (selectedSeries.length === 0) return geoData;
    return {
      type: 'FeatureCollection',
      features: geoData.features.filter((f) =>
        selectedSeries.some((sel) => sel.value === f.properties.series_id)
      ),
    };
  }, [geoData, selectedSeries]);

  // Вспомогательная функция: получить подробные данные дома по ID
  const fetchHouseById = async (id) => {
    try {
      const res = await axios.get(`${API_BASE_URL}/building/get_build_by_id/${id}`);
      return res.data;
    } catch (err) {
      console.error('Ошибка получения дома по id:', err);
      return null;
    }
  };

  // При загрузке geoData проверяем, есть ли сохранённый id дома для восстановления панели
  useEffect(() => {
    const tryRestore = async () => {
      if (!map || !geoData || seriesList.length === 0) return;
      const storedId = Number(localStorage.getItem('openedHouseId'));
      if (storedId && !selectedHouseId.current) {
        // Проверяем, есть ли в отфильтрованном geoData дом с таким id
        const feature = geoData.features.find((f) => f.properties.id === storedId);
        if (feature) {
          // Подсветим фичу
          map.setFeatureState({ source: 'houses', id: storedId }, { selected: true });
          selectedHouseId.current = storedId;

          // Получаем подробности дома
          const houseData = await fetchHouseById(storedId);
          if (houseData) {
            const seriesObj = seriesList.find((s) => s.id === houseData.series_id);
            const seriesName = seriesObj ? seriesObj.name : '—';
            // Показ панели
            if (!hasAnimated.current) {
              setAnimatePanel(false);
              setTimeout(() => {
                setAnimatePanel(true);
                hasAnimated.current = true;
              }, 0);
            } else {
              setAnimatePanel(true);
            }
            setSelectedHouse({ ...houseData, series_name: seriesName });
          }
        } else {
          // Если дом сейчас не отображается (фильтр), убираем из хранения
          localStorage.removeItem('openedHouseId');
        }
      }
    };
    tryRestore();
  }, [map, geoData, seriesList]);

  // Обработка кликов на карту (точка дома, кластер, пустое место)
  useEffect(() => {
    if (!map || !geoData) return;

    // При клике на дом: подсветить и открыть панель, загрузив данные по API
    const onHouseClick = async (e) => {
      const f = e.features?.[0];
      if (!f) return;
      const id = f.properties.id;

      // Сброс предыдущей подсветки
      if (selectedHouseId.current !== null && selectedHouseId.current !== id) {
        map.setFeatureState(
          { source: 'houses', id: selectedHouseId.current },
          { selected: false }
        );
      }
      // Подсветка текущей точки
      map.setFeatureState({ source: 'houses', id }, { selected: true });
      selectedHouseId.current = id;

      // Загружаем подробную информацию по дому
      const houseData = await fetchHouseById(id);
      if (!houseData) return;
      const seriesObj = seriesList.find((s) => s.id === houseData.series_id);
      const seriesName = seriesObj ? seriesObj.name : '—';

      // Анимация панели
      if (!hasAnimated.current) {
        setAnimatePanel(false);
        setTimeout(() => {
          setAnimatePanel(true);
          hasAnimated.current = true;
        }, 0);
      } else {
        setAnimatePanel(true);
      }
      setSelectedHouse({ ...houseData, series_name: seriesName });
    };

    // При клике в пустое место (или вне домов) — закрыть панель
    const onClose = (e) => {
      const hits = map.queryRenderedFeatures(e.point, {
        layers: ['unclustered-point', 'house-hover'],
      });
      if (hits.length === 0 && selectedHouse) {
        if (selectedHouseId.current !== null) {
          map.setFeatureState(
            { source: 'houses', id: selectedHouseId.current },
            { selected: false }
          );
          selectedHouseId.current = null;
        }
        setAnimatePanel(false);
        setTimeout(() => {
          setSelectedHouse(null);
          hasAnimated.current = false;
          // Убираем сохранённый id, если панель закрыта вручную
          localStorage.removeItem('openedHouseId');
        }, 250);
      }
    };

    // Обработка кластеров — зум
    const onClusterClick = (e) => {
      const feature = e.features[0];
      const clusterId = feature.properties.cluster_id;
      map.getSource('houses').getClusterExpansionZoom(clusterId, (err, zoom) => {
        if (err) return;
        const targetZoom = Math.min(zoom + 2, map.getMaxZoom());
        map.easeTo({
          center: feature.geometry.coordinates,
          zoom: targetZoom,
          duration: 600,
        });
      });
    };
    const onClusterEnter = () => {
      map.getCanvas().style.cursor = 'pointer';
    };
    const onClusterLeave = () => {
      map.getCanvas().style.cursor = '';
    };

    map.on('click', 'unclustered-point', onHouseClick);
    map.on('click', onClose);
    map.on('click', 'clusters', onClusterClick);
    map.on('mouseenter', 'clusters', onClusterEnter);
    map.on('mouseleave', 'clusters', onClusterLeave);

    return () => {
      map.off('click', 'unclustered-point', onHouseClick);
      map.off('click', onClose);
      map.off('click', 'clusters', onClusterClick);
      map.off('mouseenter', 'clusters', onClusterEnter);
      map.off('mouseleave', 'clusters', onClusterLeave);
    };
  }, [map, geoData, seriesList, selectedHouse]);

  // Стиль слоя домов
  const houseLayer = {
    id: 'unclustered-point',
    type: 'circle',
    source: 'houses',
    filter: ['!', ['has', 'point_count']],
    paint: {
      'circle-color': [
        'case',
        ['boolean', ['feature-state', 'selected'], false],
        '#b23e13',
        '#eb5717',
      ],
      'circle-radius': [
        'case',
        ['boolean', ['feature-state', 'selected'], false],
        12,
        6,
      ],
      'circle-stroke-color': '#ffffff',
      'circle-stroke-width': 1.5,
      'circle-opacity': 1,
    },
  };

  // Компонент для чекбоксов в списке серий
  const OptionCheckbox = (props) => (
    <components.Option {...props}>
      <div
        style={{
          width: 14,
          height: 14,
          marginRight: 8,
          border: '1px solid #3B3B40',
          borderRadius: 3,
          backgroundColor: props.isSelected ? '#FF7F00' : 'transparent',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {props.isSelected && (
          <span style={{ color: '#15171D', fontSize: 12, lineHeight: 1 }}>
            ✓
          </span>
        )}
      </div>
      <span style={{ color: '#e5e7eb' }}>{props.label}</span>
    </components.Option>
  );

  return (
    <>
      {/* Выпадающий список для фильтрации по серии */}
      <div className="series-filter-dropdown">
        <Select
          className="modal-select"
          classNamePrefix="modal-select"
          options={seriesOptions}
          isMulti
          isSearchable
          openMenuOnClick
          closeMenuOnSelect={false}
          hideSelectedOptions={false}
          placeholder="Выберите серию"
          menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
          menuPosition="absolute"
          components={{
            Option: OptionCheckbox,
            MultiValue: () => null,
            MultiValueRemove: () => null,
          }}
          styles={{
            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
            control: (base) => ({
              ...base,
              backgroundColor: '#15171D',
              border: '1px solid #3B3B40',
              borderRadius: 8,
              minHeight: 42,
              padding: '4px 8px',
              cursor: 'text',
            }),
            placeholder: (base) => ({ ...base, color: '#8b919d' }),
            dropdownIndicator: (base) => ({ ...base, color: '#e5e7eb' }),
            indicatorSeparator: () => ({ display: 'none' }),
            menu: (base) => ({
              ...base,
              backgroundColor: '#2a2c33',
              border: '1px solid #3B3B40',
              marginTop: 4,
            }),
            option: (base, state) => ({
              ...base,
              backgroundColor: state.isFocused ? '#3B3B40' : '#2a2c33',
              color: '#e5e7eb',
              display: 'flex',
              alignItems: 'center',
            }),
          }}
          value={selectedSeries}
          onChange={setSelectedSeries}
        />
      </div>

      {/* Отрисовка точек домов на карте */}
      {filteredGeoData && (
        <Source
          id="houses"
          type="geojson"
          data={filteredGeoData}
          cluster={true}
          clusterRadius={50}
          clusterMaxZoom={14}
        >
          {/* Кластеры */}
          <Layer
            id="clusters"
            type="circle"
            filter={['has', 'point_count']}
            paint={{
              'circle-color': '#FF740F',
              'circle-stroke-color': '#ffffff',
              'circle-stroke-width': 2,
              'circle-radius': ['step', ['get', 'point_count'], 16, 10, 20, 50, 28],
              'circle-opacity': 0.9,
            }}
          />
          {/* Подписи кластеров */}
          <Layer
            id="cluster-count"
            type="symbol"
            filter={['has', 'point_count']}
            layout={{
              'text-field': '{point_count_abbreviated}',
              'text-font': ['Arial Unicode MS Bold'],
              'text-size': 14,
              'text-offset': [0, 0.1],
              'text-anchor': 'center',
            }}
            paint={{
              'text-color': '#ffffff',
              'text-halo-color': '#15171D',
              'text-halo-width': 2,
            }}
          />
          {/* Одиночные точки домов */}
          <Layer {...houseLayer} />
          {/* Ловушка для кликов вне домов */}
          <Layer
            id="house-hover"
            type="circle"
            filter={['!', ['has', 'point_count']]}
            paint={{ 'circle-radius': 12, 'circle-opacity': 0 }}
          />
        </Source>
      )}

      {/* Панель информации о выбранном доме */}
      {selectedHouse && (
        <div
          className={
            'collector-info-panel' +
            (animatePanel ? ' open' : '') +
            ' no-image'
          }
        >
          <button
            className="panel-close"
            onClick={() => {
              if (selectedHouseId.current !== null) {
                map.setFeatureState(
                  { source: 'houses', id: selectedHouseId.current },
                  { selected: false }
                );
                selectedHouseId.current = null;
              }
              setAnimatePanel(false);
              setTimeout(() => {
                setSelectedHouse(null);
                hasAnimated.current = false;
                // Очистим сохранённый id
                localStorage.removeItem('openedHouseId');
              }, 250);
            }}
          >
            ×
          </button>
          <div className="info-body">
            <div className="TTitle">{selectedHouse.address}</div>
            <div>Серия дома: {selectedHouse.series_name}</div>
            <div>Этажей: {selectedHouse.floors}</div>
            <div>Квартир: {selectedHouse.count_appartaments}</div>
            <div>Год постройки: {selectedHouse.construction_year}</div>
            <div className="guide-text">
              Для просмотра подробной информации нажмите кнопку «Просмотр информации».
            </div>
            <button
              className="primary-btn"
              onClick={() => {
                // Сохраняем id дома перед переходом в детали
                localStorage.setItem('openedHouseId', selectedHouse.id);
                navigate(`/db-house/${selectedHouse.id}`, {
                  state: { ...selectedHouse, source: 'map' },
                });
              }}
            >
              Просмотр информации &gt;
            </button>
          </div>
        </div>
      )}
    </>
  );
}
